import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { severeWeatherReportService, PUBLIC_API_ROOT } from '../services/api';
import { SevereWeatherReport } from '../types';

const REPORT_STATUSES: SevereWeatherReport['status'][] = ['submitted', 'reviewing', 'verified', 'dismissed'];

const StaffReportsPage: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const canAccess = !!user?.permissions?.includes('manage_weather_reports');
  const [reports, setReports] = useState<SevereWeatherReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [deleteReason, setDeleteReason] = useState('');

  useEffect(() => {
    if (!canAccess) {
      setLoading(false);
      return;
    }

    setLoading(true);
    severeWeatherReportService
      .list()
      .then((response) => setReports(response.data))
      .catch(() => setError('Failed to load weather reports'))
      .finally(() => setLoading(false));
  }, [canAccess]);

  const handleStatusChange = async (reportId: string, status: SevereWeatherReport['status']) => {
    setSavingId(reportId);
    setError(null);
    setSuccess(null);

    try {
      const response = await severeWeatherReportService.updateStatus(reportId, status);
      setReports((current) => current.map((report) => (report.id === reportId ? response.data.report : report)));
    } catch (requestError: any) {
      setError(requestError.response?.data?.error || 'Failed to update report status');
    } finally {
      setSavingId(null);
    }
  };

  const handleRemove = async (reportId: string) => {
    const reason = deleteTargetId === reportId ? deleteReason.trim() : '';
    if (!reason) {
      setError('Enter a removal reason before deleting a report');
      setSuccess(null);
      return;
    }

    setSavingId(reportId);
    setError(null);
    setSuccess(null);

    try {
      await severeWeatherReportService.delete(reportId, reason);
      setReports((current) => current.filter((report) => report.id !== reportId));
      setDeleteTargetId(null);
      setDeleteReason('');
      setSuccess('Report removed from the inbox.');
    } catch (requestError: any) {
      setError(requestError.response?.data?.error || 'Failed to remove report');
    } finally {
      setSavingId(null);
    }
  };

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!canAccess) {
    return <Navigate to="/access-denied" replace />;
  }

  return (
    <div className="page-shell">
      <div className="container">
        <div className="page-intro">
          <div>
            <p className="eyebrow">Staff operations</p>
            <h1 className="page-heading">Severe weather reports inbox</h1>
            <p className="page-copy">
              Review public reports, inspect attachments, and move reports through review, verification, or dismissal.
            </p>
          </div>
        </div>

        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}
        {loading && <div className="card">Loading reports...</div>}
        {!loading && reports.length === 0 && <div className="card">No public severe weather reports have been submitted yet.</div>}

        {!loading && reports.length > 0 && (
          <div className="report-grid">
            {reports.map((report) => (
              <article key={report.id} className="report-card">
                <div className="module-header">
                  <div>
                    <p className="eyebrow">{report.report_type}</p>
                    <h2 className="section-heading">{report.damage_type}</h2>
                    <p className="module-note">{report.location_description}</p>
                  </div>
                  <span className={`module-status ${report.status === 'verified' ? 'module-status-live' : report.status === 'dismissed' ? 'module-status-limited' : 'module-status-soon'}`}>
                    {report.status}
                  </span>
                </div>

                <div className="detail-meta">
                  <span><strong>When:</strong> {new Date(report.occurred_at).toLocaleString()}</span>
                  <span><strong>Submitted:</strong> {new Date(report.created_at).toLocaleString()}</span>
                  {report.reporter_name && <span><strong>Name:</strong> {report.reporter_name}</span>}
                </div>

                <p className="detail-content">{report.report_details}</p>

                <div className="form-group">
                  <span className="form-label">Attachments</span>
                  <div className="attachment-list">
                    {report.attachments.map((attachment) => (
                      <a key={attachment} className="attachment-link" href={`${PUBLIC_API_ROOT}${attachment}`} target="_blank" rel="noreferrer">
                        Open attachment
                      </a>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor={`status-${report.id}`}>Review status</label>
                  <select
                    id={`status-${report.id}`}
                    className="form-input compact-input"
                    value={report.status}
                    disabled={savingId === report.id}
                    onChange={(event) => handleStatusChange(report.id, event.target.value as SevereWeatherReport['status'])}
                  >
                    {REPORT_STATUSES.map((status) => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor={`remove-reason-${report.id}`}>Remove from inbox</label>
                  <div className="delete-user-stack">
                    <input
                      id={`remove-reason-${report.id}`}
                      className="form-input compact-input"
                      placeholder="dupe, spam, invalid, etc"
                      value={deleteTargetId === report.id ? deleteReason : ''}
                      disabled={savingId === report.id}
                      onFocus={() => {
                        setDeleteTargetId(report.id);
                        setDeleteReason('');
                      }}
                      onChange={(event) => {
                        setDeleteTargetId(report.id);
                        setDeleteReason(event.target.value);
                      }}
                    />
                    <button
                      type="button"
                      className="btn btn-danger"
                      disabled={savingId === report.id}
                      onClick={() => handleRemove(report.id)}
                    >
                      {savingId === report.id ? 'Removing...' : 'Remove'}
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default StaffReportsPage;